import type {
  TargetingRule,
  Theme,
  Tour,
  TourSnapshotStep,
  Traits,
  TriggerRule,
  StepButton,
  StepButtonKind,
  StepRequirement,
  TourAction,
} from './types';
import { fireCompletionAnimation, schedulePendingCompletion } from './animations';
import { renderInlineMarkdownInto } from './markdown';

// ---------- Targeting + trigger evaluation ----------

function urlMatches(
  pattern: string,
  match: 'exact' | 'contains' | 'regex',
  url: string,
): boolean {
  if (match === 'exact') return url === pattern;
  if (match === 'contains') return url.indexOf(pattern) !== -1;
  try {
    return new RegExp(pattern).test(url);
  } catch {
    return false;
  }
}

function traitMatches(rule: Extract<TargetingRule, { type: 'trait' }>, traits: Traits): boolean {
  const v = traits[rule.key];
  if (rule.op === 'exists') return v !== undefined && v !== null;
  if (v === undefined || v === null) return false;
  const sv = String(v);
  const target = rule.value ?? '';
  if (rule.op === 'eq') return sv === target;
  if (rule.op === 'neq') return sv !== target;
  if (rule.op === 'contains') return sv.indexOf(target) !== -1;
  return false;
}

export function tourApplies(tour: Tour, url: string, traits: Traits): boolean {
  return targetingApplies(tour.snapshot.targeting, url, traits);
}

// Generic targeting evaluator. Same semantics as tourApplies (all rules AND-ed)
// but pulled out so the checklist runtime can reuse it without coupling to
// the tour shape.
export function targetingApplies(
  rules: TargetingRule[] | undefined,
  url: string,
  traits: Traits,
): boolean {
  if (!rules || rules.length === 0) return true;
  for (const rule of rules) {
    if (rule.type === 'url_match') {
      if (!urlMatches(rule.pattern, rule.match, url)) return false;
    } else if (rule.type === 'trait') {
      if (!traitMatches(rule, traits)) return false;
    }
  }
  return true;
}

export function triggerFires(
  triggers: TriggerRule[],
  context: { url: string; kind: 'page_load' | 'route_change' | 'event'; eventName?: string },
): boolean {
  for (const t of triggers) {
    if (t.type === 'page_load' && (context.kind === 'page_load' || context.kind === 'route_change')) {
      return true;
    }
    if (t.type === 'url_match' && (context.kind === 'page_load' || context.kind === 'route_change')) {
      if (urlMatches(t.pattern, t.match, context.url)) return true;
    }
    if (t.type === 'event' && context.kind === 'event' && t.name === context.eventName) {
      return true;
    }
  }
  return false;
}

// ---------- Rendering ----------

// A single global runtime instance per SDK load. Holds the currently-shown
// tour + step index, the shadow DOM root, and handlers.
type RuntimeState = {
  host: HTMLElement | null;
  shadow: ShadowRoot | null;
  current: { tour: Tour; stepIdx: number } | null;
  // Tour ids that have been shown this session - prevents the same page-load
  // tour re-firing after a step advance happens mid-tour (we would reevaluate
  // on route change / track and match again). A seen tour is considered
  // "handled" until the session reloads. Completion analytics already fire.
  shownTourIds: Set<string>;
  // Cleanup function for the scroll/resize listeners on the currently-shown
  // tooltip. Called before rendering the next step and on tour close.
  positionCleanup: (() => void) | null;
  theme: Theme | null;
  // Track-event watchers for `requireToAdvance: { type: 'event' }` steps.
  // The client's `track(name)` calls notifyTrackEvent(name) below, which
  // dispatches to every watcher. Each step adds at most one watcher and
  // removes it during the step's positionCleanup.
  eventWatchers: Set<(name: string) => void>;
};

const state: RuntimeState = {
  host: null,
  shadow: null,
  current: null,
  shownTourIds: new Set(),
  positionCleanup: null,
  theme: null,
  eventWatchers: new Set(),
};

/**
 * Called by the client's `track()` implementation so step requirements of
 * the form `{ type: 'event', name }` can be satisfied without round-tripping
 * to the server. Safe to call before any tour is active - the watcher set
 * is just empty and the call is a no-op.
 */
export function notifyTrackEvent(name: string): void {
  for (const watcher of state.eventWatchers) {
    try {
      watcher(name);
    } catch {
      // A throwing watcher shouldn't kill the others; never propagate up
      // into the host's track() call either.
    }
  }
}

type FieldElement = HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement;

function isFieldElement(el: Element): el is FieldElement {
  const tag = el.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT';
}

/**
 * Find the form field associated with an anchor. Three cases:
 *   - The anchor IS a field - use it directly.
 *   - The anchor wraps a single field (label, .form-field container, etc.) -
 *     this is the common case where the picker captured a stable parent
 *     instead of the input. Use the first descendant.
 *   - Neither - return null; the caller treats this as "requirement can't
 *     be satisfied" and leaves the Next button disabled.
 */
function findFieldForAnchor(anchor: HTMLElement): FieldElement | null {
  if (isFieldElement(anchor)) return anchor;
  const descendant = anchor.querySelector('input, textarea, select');
  if (descendant && isFieldElement(descendant)) return descendant;
  return null;
}

/**
 * Watch the given requirement and fire `onSatisfy()` exactly once when it's
 * met. Returns a teardown function the caller invokes during step cleanup
 * to detach DOM listeners / drop event watchers.
 *
 * `anchor_has_value` accepts both an anchor that IS a form field and an
 * anchor that wraps one - pickers often capture a stable parent (label,
 * form-row container) rather than the bare input. If neither the anchor
 * nor any descendant is a field, we return a no-op teardown and the Next
 * button stays disabled forever. That's the right failure mode - silently
 * auto-enabling would defeat the point of the gate.
 */
function watchRequirement(
  req: StepRequirement,
  anchor: HTMLElement | null,
  onSatisfy: () => void,
): () => void {
  if (req.type === 'anchor_has_value') {
    if (!anchor) return () => {};
    const field = findFieldForAnchor(anchor);
    if (!field) return () => {};
    let satisfied = false;
    const check = () => {
      if (satisfied) return;
      const v = field.value ?? '';
      if (v.trim().length > 0) {
        satisfied = true;
        onSatisfy();
      }
    };
    // Initial check - the field may already have a value when the step
    // renders (e.g. user navigated back, browser autofill, draft state).
    check();
    field.addEventListener('input', check);
    field.addEventListener('change', check);
    return () => {
      field.removeEventListener('input', check);
      field.removeEventListener('change', check);
    };
  }
  if (req.type === 'event') {
    const wanted = req.name;
    let satisfied = false;
    const watcher = (name: string) => {
      if (satisfied) return;
      if (name === wanted) {
        satisfied = true;
        onSatisfy();
      }
    };
    state.eventWatchers.add(watcher);
    return () => {
      state.eventWatchers.delete(watcher);
    };
  }
  return () => {};
}

const RADIUS_PX: Record<Theme['radius'], string> = {
  sm: '6px',
  md: '10px',
  lg: '14px',
};

const FONT_SIZE_PX: Record<Theme['fontSize'], string> = {
  compact: '12px',
  default: '14px',
  roomy: '16px',
};

const BUTTONS_JUSTIFY: Record<Theme['buttonsAlign'], string> = {
  left: 'flex-start',
  center: 'center',
  right: 'flex-end',
};

const SYSTEM_FONT_STACK =
  "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif";

/**
 * Apply a theme to the shadow DOM. Called by the client when `/v1/session`
 * or `/v1/identify` returns a theme. Safe to call before the shadow exists -
 * the theme is stashed and re-applied at mount.
 *
 * Implementation: we inject a dedicated <style> element inside the shadow
 * root that sets CSS custom properties on :host. This is the canonical
 * pattern for theming shadow DOM - setting properties via
 * `host.style.setProperty()` on the outer host element is subtly unreliable
 * because the host has `style="all: initial"` inline, and browsers differ in
 * whether custom properties set afterward inherit through the shadow
 * boundary in that situation. Styling via a shadow-internal :host rule
 * avoids the ambiguity entirely.
 */
export function applyTheme(theme: Theme) {
  state.theme = theme;

  // The host element has `all: initial` applied inline for isolation, which
  // inline-wins over any :host rule in shadow CSS. That's fine for most
  // styling - custom properties cross the boundary unaffected - but any
  // regular property we *want* to apply to the host element itself has to
  // be set as a later inline style so later-inline-wins tips our way.
  // font-size (for em-based text scaling) and font-family ('inherit' mode)
  // both need this treatment.
  if (state.host) {
    const fontSize = FONT_SIZE_PX[theme.fontSize] ?? FONT_SIZE_PX.default;
    state.host.style.fontSize = fontSize;
    if (theme.fontFamily === 'inherit') {
      state.host.style.fontFamily = 'inherit';
    } else {
      state.host.style.fontFamily = '';
    }
  }

  if (!state.shadow) return;

  let style = state.shadow.getElementById('steppr-theme') as HTMLStyleElement | null;
  if (!style) {
    style = document.createElement('style');
    style.id = 'steppr-theme';
    state.shadow.appendChild(style);
  }

  const radius = RADIUS_PX[theme.radius] ?? RADIUS_PX.md;
  const btnRadius =
    theme.radius === 'sm' ? '4px' : theme.radius === 'md' ? '8px' : '10px';
  const fontSize = FONT_SIZE_PX[theme.fontSize] ?? FONT_SIZE_PX.default;
  const buttonsJustify =
    BUTTONS_JUSTIFY[theme.buttonsAlign] ?? BUTTONS_JUSTIFY.right;
  const fontFamily = theme.fontFamily === 'inherit' ? 'inherit' : SYSTEM_FONT_STACK;

  style.textContent = `:host {
  --steppr-primary: ${theme.primaryColor};
  --steppr-radius: ${radius};
  --steppr-btn-radius: ${btnRadius};
  --steppr-font-size: ${fontSize};
  --steppr-text-align: ${theme.textAlign};
  --steppr-buttons-justify: ${buttonsJustify};
  --steppr-font-family: ${fontFamily};
}`;
}

// Minimal scoped CSS for the shadow root. Designed to look clean on any host
// site, fixed-position so layout can't affect it, z-index high enough to beat
// most SaaS app overlays but below native browser UI.
//
// NOTE: do not use `all: initial` on :host - it resets font-family to the
// browser default (serif on most platforms), so Times New Roman leaks through.
// Shadow DOM already isolates page CSS from the tour UI, so explicit font
// declarations here are sufficient. `font: inherit` on buttons is required
// because browsers apply their own font to form controls regardless.
const SHADOW_CSS = `
:host {
  font-size: var(--steppr-font-size, 14px);
  line-height: 1.5;
  color: #0f172a;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}
/* Pin font-family via a CSS var so theme can swap stacks (system / inherit)
 * without fighting the host element's inline style="all: initial". The
 * universal selector is explicit about fonts so shadow descendants never
 * fall back to browser defaults (serif). */
* {
  box-sizing: border-box;
  font-family: var(--steppr-font-family, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif);
}
button, input, textarea { font: inherit; }

.backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.55);
  backdrop-filter: blur(2px);
  -webkit-backdrop-filter: blur(2px);
  z-index: 2147483600;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Spotlight = four strips framing the anchor. Together they dim + blur the
 * entire viewport except the anchor rect itself, which stays bright and
 * interactive because no layer covers it. */
.spotlight-strip {
  position: fixed;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(2px);
  -webkit-backdrop-filter: blur(2px);
  z-index: 2147483600;
  transition: top 120ms ease, left 120ms ease, width 120ms ease, height 120ms ease;
}

.modal {
  background: #ffffff;
  border-radius: var(--steppr-radius, 12px);
  box-shadow: 0 20px 40px -15px rgba(0, 0, 0, 0.35);
  padding: 24px 26px;
  max-width: 460px;
  width: calc(100vw - 32px);
  position: relative;
}

.tooltip {
  position: fixed;
  z-index: 2147483601;
  background: #ffffff;
  border-radius: var(--steppr-radius, 10px);
  box-shadow: 0 12px 28px -8px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(15, 23, 42, 0.08);
  padding: 16px 18px;
  max-width: 340px;
  width: max-content;
  /* arrow x is set per-render via --arrow-x (computed from anchor center) */
  --arrow-x: 50%;
}

.tooltip::before {
  content: '';
  position: absolute;
  width: 12px;
  height: 12px;
  background: #ffffff;
  transform: rotate(45deg);
  box-shadow: 0 0 0 1px rgba(15, 23, 42, 0.08);
  left: var(--arrow-x);
  margin-left: -6px;
}

.tooltip[data-placement="top"]::before {
  bottom: -6px;
  box-shadow: 2px 2px 0 -1px rgba(15, 23, 42, 0.08);
}
.tooltip[data-placement="bottom"]::before {
  top: -6px;
  box-shadow: -1px -1px 0 -1px rgba(15, 23, 42, 0.08);
}

.image {
  display: block;
  width: 100%;
  max-height: 220px;
  object-fit: cover;
  border-radius: calc(var(--steppr-radius, 8px) - 2px);
  margin: 0 0 12px;
  background: #f1f5f9;
}
.modal .image { max-height: 280px; }
.tooltip .image { max-height: 160px; }
/* Bottom-position image: breathing room above (we sit below the body)
   AND below (the buttons row has no top-margin of its own, so without
   this they crash into the image). Selector matches an .image whose
   previous sibling is .body, or an .image-link (the anchor wrapper
   added when imageHref is set) in the same position. */
.body + .image,
.body + .image-link { margin: 4px 0 16px; }
/* When the image is wrapped in a link, the anchor inherits the image's
   slot. Inherit margin from the inner <img> baseline rules so spacing
   matches the unwrapped case, and reset typography so the wrapper
   doesn't pick up underline / colour from any host-page styles that
   pierced the shadow root (none should, but defence in depth). */
.image-link {
  display: block;
  text-decoration: none;
  color: inherit;
  margin: 0 0 12px;
}
.image-link .image { margin: 0; }
/* When the wrapper is bottom-positioned the outer margin block above
   handles spacing; the inner <img> stays at zero so we don't double up. */
.body + .image-link .image { margin: 0; }

.title {
  margin: 0 0 6px;
  font-size: 1.14em;
  font-weight: 600;
  letter-spacing: -0.005em;
  line-height: 1.3;
  text-align: var(--steppr-text-align, left);
}

.body {
  margin: 0 0 16px;
  font-size: 1em;
  line-height: 1.5;
  color: #475569;
  white-space: pre-wrap;
  text-align: var(--steppr-text-align, left);
}

.modal .title { font-size: 1.28em; }
.modal .body { font-size: 1.07em; }

.buttons {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: var(--steppr-buttons-justify, flex-end);
}

.btn {
  font: inherit;
  font-size: 0.93em;
  font-weight: 500;
  padding: 7px 14px;
  border-radius: var(--steppr-btn-radius, 8px);
  border: 1px solid #e2e8f0;
  background: #ffffff;
  color: #0f172a;
  cursor: pointer;
  transition: background 120ms ease, border-color 120ms ease, filter 120ms ease;
}
.btn:hover { background: #f8fafc; }

.btn[data-kind="next"] {
  background: var(--steppr-primary, #0f172a);
  color: #ffffff;
  border-color: var(--steppr-primary, #0f172a);
}
.btn[data-kind="next"]:hover { filter: brightness(1.1); }
.btn[disabled] {
  opacity: 0.5;
  cursor: not-allowed;
  pointer-events: none;
  filter: grayscale(0.4);
}

/* Inline-editable affordance for preview mode. The runtime only sets
   contenteditable on title / body when RuntimeCallbacks.onTextEdit is
   provided (preview mode), so production tours never see this. */
[contenteditable]:hover {
  background: rgba(56, 189, 248, 0.10);
  border-radius: 4px;
  outline: 1px dashed rgba(56, 189, 248, 0.45);
  outline-offset: 2px;
  cursor: text;
}
[contenteditable]:focus {
  background: rgba(56, 189, 248, 0.06);
  outline: 2px solid #38bdf8;
  outline-offset: 2px;
  border-radius: 4px;
  cursor: text;
}
/* Ghost text for empty editable fields - lets authors click in and add a
   title or description that the step was missing. pointer-events:none so
   clicks fall through to the contenteditable behind it. */
[contenteditable]:empty::before {
  content: attr(data-placeholder);
  color: rgba(100, 116, 139, 0.7);
  font-style: italic;
  pointer-events: none;
}

.close {
  position: absolute;
  top: 10px;
  right: 10px;
  width: 28px;
  height: 28px;
  border: none;
  background: transparent;
  color: #64748b;
  cursor: pointer;
  border-radius: 6px;
  font-size: 18px;
  line-height: 1;
}
.close:hover { background: #f1f5f9; color: #0f172a; }

.badge {
  display: inline-block;
  font-size: 0.72em;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: #94a3b8;
  margin-bottom: 8px;
  font-weight: 600;
}

.powered {
  margin-top: 14px;
  text-align: right;
  font-size: 0.78em;
  color: #94a3b8;
}
.powered a { color: inherit; text-decoration: none; }
.powered a:hover { text-decoration: underline; }

/* Hotspot = small pulsing dot anchored to the corner of a target element.
 * Passive: no backdrop, no spotlight, host page stays fully interactive.
 * Clicking the dot reveals the tour's tooltip content near the anchor. */
.hotspot-dot {
  position: fixed;
  z-index: 2147483601;
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background: var(--steppr-primary, #2563eb);
  box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.9), 0 1px 4px rgba(0, 0, 0, 0.25);
  cursor: pointer;
  border: none;
  padding: 0;
}
.hotspot-dot::before {
  content: '';
  position: absolute;
  inset: 0;
  border-radius: 50%;
  background: var(--steppr-primary, #2563eb);
  opacity: 0.55;
  animation: steppr-hotspot-pulse 1.8s ease-out infinite;
  pointer-events: none;
}
.hotspot-dot:focus-visible {
  outline: 2px solid #0f172a;
  outline-offset: 2px;
}
@keyframes steppr-hotspot-pulse {
  0%   { transform: scale(1);   opacity: 0.55; }
  100% { transform: scale(2.6); opacity: 0;    }
}
`;

function ensureShadow(): ShadowRoot {
  if (state.shadow) return state.shadow;
  const host = document.createElement('div');
  host.id = '__steppr_root__';
  host.style.all = 'initial';
  const shadow = host.attachShadow({ mode: 'open' });
  const style = document.createElement('style');
  style.textContent = SHADOW_CSS;
  shadow.appendChild(style);
  document.body.appendChild(host);
  state.host = host;
  state.shadow = shadow;
  // Stop pointer events from leaking out of the tour overlay into the
  // customer's app - most importantly, customer modals' click-outside
  // handlers see our card / Next-button clicks as "outside" and close
  // themselves, taking the tour's anchor with them. Bubble-phase
  // listeners on the host run AFTER any backdrop/strip dismiss handlers
  // we attached inside the shadow, so the dismiss-on-backdrop opt-in
  // still works. Same pattern we use on the picker overlay.
  for (const evt of [
    'click',
    'mousedown',
    'mouseup',
    'pointerdown',
    'pointerup',
    'touchstart',
  ]) {
    host.addEventListener(evt, (e) => e.stopPropagation());
  }

  // Bubble-phase focus event isolation on the host. focus/blur don't
  // bubble; focusin/focusout do. Stopping these here keeps host-page
  // listeners from observing focus traffic inside our overlay - but
  // it does NOT stop focus-trap libraries that listen via capture or
  // poll document.activeElement on requestAnimationFrame (e.g.
  // react-focus-lock as used by Radix Dialog). Modal-anchored steps
  // need an additional structural fix; see editable-focus-shield below.
  for (const evt of ['focusin', 'focusout']) {
    host.addEventListener(evt, (e) => e.stopPropagation());
  }

  // Capture-phase focus stops on document. We only intercept the
  // INCOMING side (focus, focusin) - not the outgoing side
  // (blur/focusout) - because stopImmediatePropagation in capture
  // also halts the target phase, and our bindEditable blur listener
  // sits on the target. Blocking blur there silently broke commits.
  // Stopping incoming focus events helps against event-only focus
  // traps; rAF-polling traps need the editable-focus-shield approach
  // (separate effort) to fully escape.
  const onFocusInCapture = (e: Event) => {
    const t = e.target as Node | null;
    if (t && (t === host || host.contains(t))) {
      e.stopImmediatePropagation();
    }
  };
  for (const evt of ['focus', 'focusin']) {
    document.addEventListener(evt, onFocusInCapture, true);
  }
  // If a theme arrived before the first render, apply it now that the host
  // element exists.
  if (state.theme) applyTheme(state.theme);
  return shadow;
}

// Exported for the marketing demo bundle (demo.ts), which swaps flows on
// demand; the customer runtime only closes tours from inside a step.
export function closeTour() {
  if (state.positionCleanup) {
    state.positionCleanup();
    state.positionCleanup = null;
  }
  if (!state.shadow) return;
  // Remove everything except <style> nodes. The base stylesheet plus the
  // theme-override stylesheet both live under the shadow root and must
  // survive tour teardown so the next render styles correctly.
  const styles = Array.from(state.shadow.querySelectorAll('style'));
  state.shadow.replaceChildren(...styles);
  state.current = null;
}

function buttonsForStep(
  step: TourSnapshotStep,
  stepIdx: number,
  total: number,
): StepButton[] {
  const cfg = step.config ?? {};
  const show = cfg.showButtons ?? true;
  if (!show) return [];
  const provided = cfg.buttons as StepButton[] | undefined;
  if (provided && provided.length > 0) return provided;
  // Default: single "Next" that becomes "Finish" on the last step.
  const label = stepIdx === total - 1 ? 'Finish' : 'Next';
  return [{ kind: 'next', label }];
}

function queryAnchor(selector: string | null | undefined): HTMLElement | null {
  if (!selector) return null;
  try {
    return document.querySelector(selector) as HTMLElement | null;
  } catch {
    return null;
  }
}

// Wait up to `timeoutMs` for an element matching `selector` to appear in the
// DOM. Uses MutationObserver + fallback interval so dynamically-mounted SPA
// targets are caught without polling at full cost. Resolves with the element
// or null if the timeout expires.
function waitForAnchor(
  selector: string,
  timeoutMs = 3000,
): Promise<HTMLElement | null> {
  return new Promise((resolve) => {
    const immediate = queryAnchor(selector);
    if (immediate) return resolve(immediate);

    let done = false;
    function finish(el: HTMLElement | null) {
      if (done) return;
      done = true;
      observer.disconnect();
      clearTimeout(timer);
      resolve(el);
    }

    const observer = new MutationObserver(() => {
      const el = queryAnchor(selector);
      if (el) finish(el);
    });
    observer.observe(document.body, { childList: true, subtree: true });
    const timer = setTimeout(() => finish(null), timeoutMs);
  });
}

// Position the tooltip relative to the anchor's current viewport rect.
// `position: fixed` on the tooltip means we must recompute on scroll/resize.
// Also points the arrow (::before) at the anchor's horizontal center, clamped
// to stay inside the tooltip so it doesn't fall off the rounded corners.
function positionTooltip(el: HTMLElement, anchor: HTMLElement) {
  const r = anchor.getBoundingClientRect();
  const margin = 10;
  const rect = el.getBoundingClientRect();
  const below = r.bottom + margin + rect.height < window.innerHeight;
  const placement: 'top' | 'bottom' = below ? 'bottom' : 'top';
  el.dataset.placement = placement;
  let top = placement === 'bottom' ? r.bottom + margin : r.top - margin - rect.height;
  // Clamp vertically within the viewport with 8px margin.
  top = Math.max(8, Math.min(top, window.innerHeight - rect.height - 8));
  let left = r.left;
  left = Math.max(8, Math.min(left, window.innerWidth - rect.width - 8));
  el.style.top = top + 'px';
  el.style.left = left + 'px';

  // Arrow points at the anchor's center, expressed in tooltip-local coords.
  // Clamped to stay 16px inside the tooltip edges so it never overhangs.
  const anchorCenterX = r.left + r.width / 2;
  const arrowX = Math.max(16, Math.min(anchorCenterX - left, rect.width - 16));
  el.style.setProperty('--arrow-x', arrowX + 'px');
}

// Build and return the four spotlight strips that frame an anchor. Positioning
// is done by `positionSpotlight` - called once on attach and again from the
// scroll/resize tracker so the hole stays aligned with the anchor.
//
// `onDismiss` is null when the tour is configured to ignore backdrop clicks
// (the default). Strips still render and dim the page; they just don't act
// as a click target.
function createSpotlight(onDismiss: (() => void) | null): HTMLElement[] {
  return ['top', 'right', 'bottom', 'left'].map(() => {
    const s = document.createElement('div');
    s.className = 'spotlight-strip';
    if (onDismiss) s.addEventListener('click', onDismiss);
    return s;
  });
}

function positionSpotlight(strips: HTMLElement[], anchor: HTMLElement) {
  const r = anchor.getBoundingClientRect();
  const W = window.innerWidth;
  const H = window.innerHeight;
  // Inflate the "hole" by a few pixels so the anchor reads as a clean focused
  // element instead of being flush against a harsh edge.
  const pad = 4;
  const top = Math.max(0, r.top - pad);
  const bottom = Math.min(H, r.bottom + pad);
  const left = Math.max(0, r.left - pad);
  const right = Math.min(W, r.right + pad);

  const [tStrip, rStrip, bStrip, lStrip] = strips;
  if (!tStrip || !rStrip || !bStrip || !lStrip) return;

  // Top strip: full width, from viewport top to cutout top.
  Object.assign(tStrip.style, {
    top: '0px',
    left: '0px',
    width: W + 'px',
    height: top + 'px',
  });
  // Bottom strip: full width, from cutout bottom to viewport bottom.
  Object.assign(bStrip.style, {
    top: bottom + 'px',
    left: '0px',
    width: W + 'px',
    height: Math.max(0, H - bottom) + 'px',
  });
  // Left strip: between top and bottom strips, left of cutout.
  Object.assign(lStrip.style, {
    top: top + 'px',
    left: '0px',
    width: left + 'px',
    height: Math.max(0, bottom - top) + 'px',
  });
  // Right strip: between top and bottom, right of cutout.
  Object.assign(rStrip.style, {
    top: top + 'px',
    left: right + 'px',
    width: Math.max(0, W - right) + 'px',
    height: Math.max(0, bottom - top) + 'px',
  });
}

// Position the hotspot dot on the top-right corner of the anchor. Half of the
// dot sits inside the anchor, half outside - the industry-standard look that
// reads as "attached to this element" without covering its content. Clamped
// to the viewport so it stays visible when the anchor is at a screen edge.
function positionHotspot(dot: HTMLElement, anchor: HTMLElement) {
  const r = anchor.getBoundingClientRect();
  const size = 14;
  const half = size / 2;
  const cx = Math.max(half, Math.min(r.right, window.innerWidth - half));
  const cy = Math.max(half, Math.min(r.top, window.innerHeight - half));
  dot.style.top = cy - half + 'px';
  dot.style.left = cx - half + 'px';
}

// Bring an anchor into view if it's not already fully visible. Uses native
// scrollIntoView with block:'center' so the element lands in the middle of
// the viewport - leaves space above and below for the tooltip regardless of
// placement choice.
function scrollAnchorIntoView(anchor: HTMLElement) {
  const r = anchor.getBoundingClientRect();
  const fullyVisible = r.top >= 0 && r.bottom <= window.innerHeight;
  if (fullyVisible) return;
  try {
    anchor.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
  } catch {
    anchor.scrollIntoView();
  }
}

// Keep the currently-visible tooltip AND the spotlight strips positioned
// correctly when the host page scrolls or resizes. Returns a cleanup fn
// that also removes the spotlight nodes from the shadow DOM.
//
// Scroll events DO NOT bubble, so a plain `window.addEventListener('scroll',
// …)` misses scrolls happening inside nested scrollable containers (common
// on modern SaaS layouts with fixed-height shells). Using capture phase on
// the document catches scroll events from any ancestor / descendant scroller.
function trackTooltipPosition(
  tooltip: HTMLElement,
  anchor: HTMLElement,
  strips: HTMLElement[],
): () => void {
  const update = () => {
    positionTooltip(tooltip, anchor);
    positionSpotlight(strips, anchor);
  };
  const scrollOpts: AddEventListenerOptions = { capture: true, passive: true };
  document.addEventListener('scroll', update, scrollOpts);
  window.addEventListener('resize', update, { passive: true });
  return () => {
    document.removeEventListener('scroll', update, scrollOpts);
    window.removeEventListener('resize', update);
    for (const s of strips) s.remove();
  };
}

type RenderDeps = {
  fireTourEvent: (action: TourAction, tourId: string, stepId?: string) => void;
  onAdvance: (dir: -1 | 1 | 'dismiss') => void;
  onTextEdit?: (stepId: string, field: 'title' | 'body', value: string) => void;
};

// Inert-shield helpers. While an editable is active we do two things:
//
//   1. Set `inert` on every direct child of document.body except our
//      shadow host. The rest of the page (including focus-trapping
//      modals) becomes non-focusable, so react-focus-lock's rAF
//      heartbeat finds element.focus() is a no-op and our editable
//      keeps focus.
//
//   2. Capture-phase stopPropagation on mousedown/click for events
//      whose target is outside our host. inert blocks event handlers
//      on the inert subtree, but the event still bubbles to document -
//      customer click-outside listeners would then see "click outside
//      the modal" and dismiss the modal. Stopping in capture cuts the
//      event off before it reaches the customer's document-level
//      listeners. Events targeting our shadow host are unaffected, so
//      the Next button etc. still work.
//
// Records prior inert state per element so we don't clobber any inert
// the host had set itself.
const editShieldPriorInert = new WeakMap<Element, boolean>();
let editShieldDepth = 0;
let activeShieldHost: HTMLElement | null = null;

function shieldPointerStopper(e: Event): void {
  const t = e.target as Node | null;
  if (!activeShieldHost) return;
  if (t && (t === activeShieldHost || activeShieldHost.contains(t))) return;
  // Click outside our overlay while editing - block it from reaching
  // the customer's listeners (modal click-outside handlers, etc).
  e.stopPropagation();
}

function applyEditShield(host: HTMLElement | null): void {
  if (!host) return;
  editShieldDepth++;
  if (editShieldDepth > 1) return;
  activeShieldHost = host;
  for (const child of Array.from(document.body.children)) {
    if (child === host) continue;
    editShieldPriorInert.set(child, child.hasAttribute('inert'));
    child.setAttribute('inert', '');
  }
  for (const evt of ['mousedown', 'click', 'pointerdown', 'touchstart']) {
    document.addEventListener(evt, shieldPointerStopper, true);
  }
}

function releaseEditShield(host: HTMLElement | null): void {
  if (!host) return;
  editShieldDepth = Math.max(0, editShieldDepth - 1);
  if (editShieldDepth > 0) return;
  for (const evt of ['mousedown', 'click', 'pointerdown', 'touchstart']) {
    document.removeEventListener(evt, shieldPointerStopper, true);
  }
  activeShieldHost = null;
  for (const child of Array.from(document.body.children)) {
    if (child === host) continue;
    const wasInert = editShieldPriorInert.get(child) ?? false;
    if (!wasInert) child.removeAttribute('inert');
    editShieldPriorInert.delete(child);
  }
}

// Wire a contenteditable handler onto a title / body element. Commits on
// blur or Enter (Shift+Enter inserts a newline as expected). Escape blurs
// without firing; callers don't get a noisy stream of intermediate values.
// Caller is responsible for setting the element's initial textContent.
function bindEditable(
  el: HTMLElement,
  stepId: string,
  field: 'title' | 'body',
  initial: string,
  onCommit: (stepId: string, field: 'title' | 'body', value: string) => void,
): void {
  el.contentEditable = 'plaintext-only';
  el.dataset.editable = field;
  // Spellcheck on for body, off for the more controlled title.
  el.spellcheck = field === 'body';
  let lastCommitted = initial;
  let pendingEscape = false;
  let shielded = false;

  // Apply the inert-shield on mousedown so it lands BEFORE the focus
  // trap can react to the focus change. preventDefault stops the
  // browser's natural click→focus, then we focus manually after the
  // shield is up - guarantees the trap finds a non-focusable modal
  // when its rAF check fires next frame.
  el.addEventListener('mousedown', (e) => {
    if (shielded) return;
    e.preventDefault();
    applyEditShield(state.host);
    shielded = true;
    el.focus();
    // Place caret where the user clicked. Default getSelection().collapse
    // would put it at the start; we approximate by selecting the end.
    const selection = el.ownerDocument?.getSelection();
    if (selection) {
      const range = el.ownerDocument!.createRange();
      range.selectNodeContents(el);
      range.collapse(false);
      selection.removeAllRanges();
      selection.addRange(range);
    }
  });

  el.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      el.blur();
    } else if (e.key === 'Escape') {
      pendingEscape = true;
      el.textContent = lastCommitted;
      el.blur();
    }
  });
  el.addEventListener('blur', () => {
    if (shielded) {
      releaseEditShield(state.host);
      shielded = false;
    }
    if (pendingEscape) {
      pendingEscape = false;
      return;
    }
    const next = (el.textContent ?? '').trim();
    if (next === lastCommitted) return;
    // Allow empty commits - authors might be clearing a field
    // intentionally, or just clicking into a placeholder slot and out
    // again without typing (no-op caught by the equality check above).
    // Escape still serves as the "I changed my mind" path.
    lastCommitted = next;
    onCommit(stepId, field, next);
  });
}

function renderStep(
  tour: Tour,
  stepIdx: number,
  deps: RenderDeps,
): void {
  const shadow = ensureShadow();
  const steps = tour.snapshot.steps;
  const maybeStep = steps[stepIdx];
  if (!maybeStep) {
    deps.onAdvance('dismiss');
    return;
  }
  // Narrowed const is referenced inside closures (buildCard) where TS's type
  // narrowing doesn't flow - so we capture a non-nullable `step` up here.
  const step: TourSnapshotStep = maybeStep;

  // Clear previous step, keep all <style> nodes (base CSS + theme overrides).
  const styles = Array.from(shadow.querySelectorAll('style'));
  shadow.replaceChildren(...styles);

  const buttons = buttonsForStep(step, stepIdx, steps.length);

  function buildCard(): HTMLDivElement {
    const card = document.createElement('div');
    card.className = step.type === 'modal' ? 'modal' : 'tooltip';

    const close = document.createElement('button');
    close.className = 'close';
    close.setAttribute('aria-label', 'Close');
    close.textContent = '×';
    close.addEventListener('click', () => deps.onAdvance('dismiss'));
    card.appendChild(close);

    if (steps.length > 1) {
      const badge = document.createElement('div');
      badge.className = 'badge';
      badge.textContent = 'Step ' + (stepIdx + 1) + ' of ' + steps.length;
      card.appendChild(badge);
    }

    // Optional image. Built once here so the same DOM node lands wherever
    // imagePosition routes it (top by default, bottom = below body but
    // above the buttons row so the CTA stays at the bottom). Lazy-loaded
    // since tours don't all render at once and we don't want to push image
    // bytes for steps the user may never reach.
    const imageUrl = step.config?.imageUrl;
    const imagePosition =
      step.config?.imagePosition === 'bottom' ? 'bottom' : 'top';
    // `imageNode` is what we actually append (either the bare <img> or an
    // <a> wrapper when the author set a link). Wrapping at construction
    // time keeps the position-routing logic untouched.
    let imageNode: HTMLElement | null = null;
    if (imageUrl && typeof imageUrl === 'string') {
      const img = document.createElement('img');
      img.className = 'image';
      img.src = imageUrl;
      img.loading = 'lazy';
      const altText =
        (typeof step.config?.imageAlt === 'string' && step.config.imageAlt) ||
        step.title ||
        '';
      img.alt = altText;
      const href =
        typeof step.config?.imageHref === 'string' && step.config.imageHref
          ? step.config.imageHref
          : null;
      if (href) {
        const a = document.createElement('a');
        a.className = 'image-link';
        a.href = href;
        // Default to new-tab so clicking the image doesn't yank the user
        // out of the tour mid-flow. 'self' is opt-in.
        if (step.config?.imageHrefTarget !== 'self') {
          a.target = '_blank';
          a.rel = 'noopener noreferrer';
        }
        a.appendChild(img);
        imageNode = a;
      } else {
        imageNode = img;
      }
    }
    if (imageNode && imagePosition === 'top') card.appendChild(imageNode);

    // In preview/edit mode (deps.onTextEdit set) we always render the
    // title and body containers - even when the step has neither - so
    // the author can click in and add new copy. Empty editable fields
    // show ghost placeholder text via the [contenteditable]:empty::before
    // CSS rule. Production / non-edit mode keeps the original behaviour
    // of skipping the element entirely when the field is empty.
    if (step.title || deps.onTextEdit) {
      const h = document.createElement('div');
      h.className = 'title';
      h.textContent = step.title ?? '';
      if (deps.onTextEdit) {
        h.dataset.placeholder = 'Add title…';
        bindEditable(h, step.id, 'title', step.title ?? '', deps.onTextEdit);
      }
      card.appendChild(h);
    }
    if (step.body || deps.onTextEdit) {
      const b = document.createElement('div');
      b.className = 'body';
      if (deps.onTextEdit) {
        // Inline-edit mode shows raw markdown source so authors can
        // type the syntax. The runtime renders formatted output to end
        // users via renderInlineMarkdownInto on the production path.
        b.textContent = step.body ?? '';
        b.dataset.placeholder = 'Add description…';
        bindEditable(b, step.id, 'body', step.body ?? '', deps.onTextEdit);
      } else if (step.body) {
        renderInlineMarkdownInto(b, step.body);
      }
      card.appendChild(b);
    }

    // Bottom-position image goes after body but before buttons so the
    // call-to-action keeps anchoring the bottom of the card.
    if (imageNode && imagePosition === 'bottom') card.appendChild(imageNode);

    if (buttons.length) {
      const row = document.createElement('div');
      row.className = 'buttons';
      for (const btn of buttons) {
        const el = document.createElement('button');
        el.className = 'btn';
        el.dataset.kind = btn.kind;
        el.textContent = btn.label;
        el.addEventListener('click', () => {
          const dir: -1 | 1 | 'dismiss' =
            btn.kind === 'next' ? 1 : btn.kind === 'prev' ? -1 : 'dismiss';
          deps.onAdvance(dir);
        });
        row.appendChild(el);
      }
      card.appendChild(row);
    }

    if (!state.theme?.hideBranding) {
      const powered = document.createElement('div');
      powered.className = 'powered';
      powered.innerHTML =
        'Powered by <a href="https://steppr.io" target="_blank" rel="noopener noreferrer">Steppr</a>';
      card.appendChild(powered);
    }

    return card;
  }

  // Clean up any prior tooltip's scroll listeners before we re-render.
  if (state.positionCleanup) {
    state.positionCleanup();
    state.positionCleanup = null;
  }

  // Step-level "Next button stays disabled until the user does X" gate.
  // Each render path calls this after building its card; the returned
  // teardown is composed into state.positionCleanup. Returns null when
  // the step has no requirement, so callers can `?? null` safely.
  function setupRequirementGate(
    card: HTMLDivElement,
    anchor: HTMLElement | null,
  ): (() => void) | null {
    const req = step.config?.requireToAdvance as StepRequirement | null | undefined;
    if (!req) return null;
    const nextBtn = card.querySelector('.btn[data-kind="next"]') as HTMLButtonElement | null;
    if (!nextBtn) return null;
    nextBtn.disabled = true;
    return watchRequirement(req, anchor, () => {
      nextBtn.disabled = false;
    });
  }

  function renderCentered(card: HTMLDivElement) {
    const backdrop = document.createElement('div');
    backdrop.className = 'backdrop';
    backdrop.appendChild(card);
    // Backdrop click only dismisses when the customer has opted in. Default
    // is "explicit close only" - accidental clicks just outside the card
    // would otherwise lose all the user's tour progress with no undo.
    if (tour.snapshot.config?.dismissOnBackdropClick === true) {
      backdrop.addEventListener('click', (e) => {
        if (e.target === backdrop) deps.onAdvance('dismiss');
      });
    }
    shadow.appendChild(backdrop);
    // Modal has no anchor - `anchor_has_value` requirements never satisfy
    // here (the whole point of the gate), but `event` requirements still
    // work. Either way, surfacing the disabled button is correct.
    const cleanupRequirement = setupRequirementGate(card, null);
    if (cleanupRequirement) {
      state.positionCleanup = cleanupRequirement;
    }
  }

  function renderHotspot(card: HTMLDivElement, anchor: HTMLElement) {
    scrollAnchorIntoView(anchor);

    const dot = document.createElement('button');
    dot.type = 'button';
    dot.className = 'hotspot-dot';
    dot.setAttribute('aria-label', 'Show tour hint');
    dot.setAttribute('aria-expanded', 'false');
    shadow.appendChild(dot);

    let tooltipOpen = false;

    function openTooltip() {
      if (tooltipOpen) return;
      shadow.appendChild(card);
      positionTooltip(card, anchor);
      tooltipOpen = true;
      dot.setAttribute('aria-expanded', 'true');
    }
    function closeTooltipOnly() {
      if (!tooltipOpen) return;
      card.remove();
      tooltipOpen = false;
      dot.setAttribute('aria-expanded', 'false');
    }

    // Reroute the tooltip's close (×) so it just closes the popover, not the
    // whole step - hotspots are designed to be re-openable until the user
    // explicitly advances or dismisses via the step buttons.
    const closeBtn = card.querySelector('.close');
    if (closeBtn) {
      const fresh = closeBtn.cloneNode(true);
      closeBtn.parentNode?.replaceChild(fresh, closeBtn);
      fresh.addEventListener('click', (e) => {
        e.stopPropagation();
        closeTooltipOnly();
      });
    }

    dot.addEventListener('click', (e) => {
      e.stopPropagation();
      if (tooltipOpen) closeTooltipOnly();
      else openTooltip();
    });

    positionHotspot(dot, anchor);

    const update = () => {
      positionHotspot(dot, anchor);
      if (tooltipOpen) positionTooltip(card, anchor);
    };
    const scrollOpts: AddEventListenerOptions = { capture: true, passive: true };
    document.addEventListener('scroll', update, scrollOpts);
    window.addEventListener('resize', update, { passive: true });

    // Ride the smooth-scroll animation so the dot tracks the anchor as it
    // moves into view. Same pattern as the tooltip renderer.
    const deadline = Date.now() + 800;
    let rafId: number | null = null;
    const tick = () => {
      positionHotspot(dot, anchor);
      if (tooltipOpen) positionTooltip(card, anchor);
      if (Date.now() < deadline) rafId = requestAnimationFrame(tick);
      else rafId = null;
    };
    rafId = requestAnimationFrame(tick);

    // Hotspot click-to-advance: same handler as anchored tooltips.
    const cleanupAdvance =
      step.config?.advanceOn === 'anchor-click'
        ? attachAnchorClickAdvance(anchor, () => deps.onAdvance(1))
        : null;

    const cleanupRequirement = setupRequirementGate(card, anchor);
    state.positionCleanup = () => {
      if (rafId !== null) cancelAnimationFrame(rafId);
      document.removeEventListener('scroll', update, scrollOpts);
      window.removeEventListener('resize', update);
      dot.remove();
      if (cleanupAdvance) cleanupAdvance();
      if (cleanupRequirement) cleanupRequirement();
    };
  }

  function renderAnchored(card: HTMLDivElement, anchor: HTMLElement) {
    scrollAnchorIntoView(anchor);
    // Build the spotlight (four dim+blur strips around the anchor). Strips
    // dismiss on click, matching modal backdrop UX.
    const strips = createSpotlight(
      tour.snapshot.config?.dismissOnBackdropClick === true
        ? () => deps.onAdvance('dismiss')
        : null,
    );
    for (const s of strips) shadow.appendChild(s);
    shadow.appendChild(card);

    // Prime initial position (may be stale until smooth scroll settles).
    positionSpotlight(strips, anchor);
    positionTooltip(card, anchor);

    // While the smooth scroll animates, re-measure every frame for ~700ms so
    // spotlight + arrow stay glued to the anchor. After that the scroll event
    // listener (registered below via trackTooltipPosition) covers ongoing
    // user-initiated scrolls.
    const deadline = Date.now() + 800;
    let rafId: number | null = null;
    const tick = () => {
      positionSpotlight(strips, anchor);
      positionTooltip(card, anchor);
      if (Date.now() < deadline) {
        rafId = requestAnimationFrame(tick);
      } else {
        rafId = null;
      }
    };
    rafId = requestAnimationFrame(tick);

    const cleanupListeners = trackTooltipPosition(card, anchor, strips);
    // If the step is configured to advance when the user clicks the anchor
    // itself, attach a one-shot capture-phase listener. Capture phase runs
    // before any of the host page's own click handlers, so we send the
    // step_complete event (with keepalive) and advance the runtime *before*
    // the native action - useful when the click navigates, because the
    // multi-page resume picks up the next step on the new page.
    const cleanupAdvance =
      step.config?.advanceOn === 'anchor-click'
        ? attachAnchorClickAdvance(anchor, () => deps.onAdvance(1))
        : null;
    const cleanupRequirement = setupRequirementGate(card, anchor);
    state.positionCleanup = () => {
      if (rafId !== null) cancelAnimationFrame(rafId);
      cleanupListeners();
      if (cleanupAdvance) cleanupAdvance();
      if (cleanupRequirement) cleanupRequirement();
    };
  }

  function attachAnchorClickAdvance(
    anchor: HTMLElement,
    advance: () => void,
  ): () => void {
    const handler = () => {
      // We don't preventDefault - the native click should still fire.
      // capture: true so we run before host handlers; once: true so a
      // second click after fast advance/render doesn't double-fire.
      advance();
    };
    anchor.addEventListener('click', handler, { capture: true, once: true });
    return () => {
      anchor.removeEventListener('click', handler, { capture: true });
    };
  }

  if (step.type === 'modal') {
    renderCentered(buildCard());
  } else if (step.type === 'hotspot') {
    // Hotspot needs an anchor. If the selector misses entirely, fall back to a
    // centered card so the tour still progresses - better to surface content
    // than to stall silently on a stale selector.
    const immediate = queryAnchor(step.selector);
    if (immediate) {
      renderHotspot(buildCard(), immediate);
    } else if (step.selector) {
      void waitForAnchor(step.selector, 3000).then((anchor) => {
        if (!state.current || state.current.tour.id !== tour.id || state.current.stepIdx !== stepIdx) {
          return;
        }
        const card = buildCard();
        if (anchor) renderHotspot(card, anchor);
        else renderCentered(card);
      });
    } else {
      renderCentered(buildCard());
    }
  } else {
    // Tooltip. Try a sync lookup first (common case: element is in the DOM
    // already). If it misses, wait up to 3s via MutationObserver for SPAs
    // that mount the target asynchronously, then fall back to centered.
    const immediate = queryAnchor(step.selector);
    if (immediate) {
      renderAnchored(buildCard(), immediate);
    } else if (step.selector) {
      void waitForAnchor(step.selector, 3000).then((anchor) => {
        // Guard: user may have advanced or dismissed while we were waiting.
        if (!state.current || state.current.tour.id !== tour.id || state.current.stepIdx !== stepIdx) {
          return;
        }
        const card = buildCard();
        if (anchor) renderAnchored(card, anchor);
        else renderCentered(card);
      });
    } else {
      renderCentered(buildCard());
    }
  }

  deps.fireTourEvent('step_view', tour.id, step.id);
}

// ---------- Orchestrator: start/advance/complete ----------

export type RuntimeCallbacks = {
  /** Post a tour_event to the API. */
  sendTourEvent: (action: TourAction, tourId: string, stepId?: string) => void;
  /**
   * Optional. When provided, the runtime renders title + body fields as
   * contenteditable and fires this callback on commit (blur or Enter).
   * Wired by preview mode so authors can iterate copy without flipping
   * back to the dashboard. Production / non-preview boots leave it
   * undefined and text stays read-only.
   */
  onTextEdit?: (stepId: string, field: 'title' | 'body', value: string) => void;
  /**
   * Optional. Fired when a tour reaches its final step and completes.
   * `nextTourId` is whatever the snapshot specifies (or null). The host
   * (client.ts) uses this to start the chained tour after the current one
   * tears down, deferring lookup of the next tour to the SDK's tour cache.
   */
  onTourComplete?: (completedTourId: string, nextTourId: string | null) => void;
};

function advance(dir: -1 | 1 | 'dismiss', cb: RuntimeCallbacks) {
  const curr = state.current;
  if (!curr) return;
  const { tour } = curr;
  const steps = tour.snapshot.steps;
  const stepId = steps[curr.stepIdx]?.id;

  if (dir === 'dismiss') {
    cb.sendTourEvent('dismiss', tour.id, stepId);
    closeTour();
    return;
  }

  const nextIdx = curr.stepIdx + dir;
  if (nextIdx < 0) return; // no-op at first step
  if (nextIdx >= steps.length) {
    cb.sendTourEvent('step_complete', tour.id, stepId);
    cb.sendTourEvent('complete', tour.id);
    // Always fire immediately so same-page completions (e.g. anchor-click
    // that closes a host modal but doesn't navigate) celebrate right away.
    fireCompletionAnimation(tour.snapshot.config, state.theme?.primaryColor);
    // For anchor-click on the last step, also stash the intent - the
    // host's own click handler may navigate / submit / reload, in which
    // case the canvas we just mounted gets torn down before any frames
    // render. The destination page picks the schedule up via
    // flushPendingCompletion. flush atomically clears the marker so
    // there's no double-fire if the page actually stays put.
    const lastStep = steps[steps.length - 1];
    if (lastStep?.config?.advanceOn === 'anchor-click') {
      schedulePendingCompletion(tour.snapshot.config, state.theme?.primaryColor);
    }
    // Tour-chain hook: hand the chain pointer to the host (client.ts) so
    // it can fire the next tour after this one tears down. We pass null
    // when there's no chain so the host can decide whether to re-evaluate
    // the auto-fire pipeline (which it does anyway on route change).
    cb.onTourComplete?.(tour.id, tour.snapshot.nextTourId ?? null);
    closeTour();
    return;
  }
  if (dir === 1) cb.sendTourEvent('step_complete', tour.id, stepId);
  state.current = { tour, stepIdx: nextIdx };
  renderStep(tour, nextIdx, {
    fireTourEvent: cb.sendTourEvent,
    onAdvance: (d) => advance(d, cb),
    onTextEdit: cb.onTextEdit,
  });
}

export function startTour(tour: Tour, cb: RuntimeCallbacks) {
  if (state.current) return; // only one tour at a time for MVP
  state.shownTourIds.add(tour.id);

  // Resume from the saved in-progress step if the API delivered one.
  // Clamp to a valid index in case the tour was re-published with fewer
  // steps after the user started - server has a similar guard but keep a
  // belt-and-braces check on the client too.
  const totalSteps = tour.snapshot.steps.length;
  const savedIdx = tour.state?.inProgressStepIndex ?? null;
  const startIdx =
    savedIdx !== null && savedIdx >= 0 && savedIdx < totalSteps ? savedIdx : 0;

  state.current = { tour, stepIdx: startIdx };
  // Per-flow theme baked into the snapshot at publish wins over the
  // workspace default applied on session boot. Falls through to the
  // existing global theme when missing (older snapshots, preview mode).
  if (tour.snapshot.theme) applyTheme(tour.snapshot.theme);
  cb.sendTourEvent('view', tour.id);
  // Only emit 'start' on a fresh run (step 0). Resumes count as a
  // continuation of an existing run for analytics purposes - otherwise
  // multi-page tours inflate start counts on every page load.
  if (startIdx === 0) cb.sendTourEvent('start', tour.id);
  renderStep(tour, startIdx, {
    fireTourEvent: cb.sendTourEvent,
    onAdvance: (d) => advance(d, cb),
  });
}

export function isTourShown(): boolean {
  return state.current !== null;
}

export function hasBeenShown(tourId: string): boolean {
  return state.shownTourIds.has(tourId);
}

// Reset the "already shown this session" flag for a tour. Used by the
// Resource Center re-launch flow - the visitor explicitly asked to replay
// the tour, so we clear the in-session memo that would otherwise no-op the
// next call to startTour.
export function forgetShownTour(tourId: string): void {
  state.shownTourIds.delete(tourId);
}

// Pick the first tour from the cache that applies and has a firing trigger.
//
// Resume gets priority: if an in-progress tour exists (state.inProgressStepIndex
// set), we surface it on page_load / route_change without checking triggers
// - the user has already started it and we just need to keep going wherever
// they end up. Targeting still applies; if their traits or current URL no
// longer match, we leave the in-progress state alone and the tour stays
// dormant until they navigate somewhere it does apply.
export function pickTour(
  tours: Tour[],
  context: { url: string; kind: 'page_load' | 'route_change' | 'event'; eventName?: string },
  traits: Traits,
): Tour | null {
  if (context.kind !== 'event') {
    for (const tour of tours) {
      if (hasBeenShown(tour.id)) continue;
      // Dismissed/completed tours stay in the list so the Resource Center can
      // offer them, but we never auto-fire them again.
      if (tour.state?.dismissed || tour.state?.completed) continue;
      const idx = tour.state?.inProgressStepIndex;
      if (idx === null || idx === undefined) continue;
      if (!tourApplies(tour, context.url, traits)) continue;
      return tour;
    }
  }
  for (const tour of tours) {
    if (hasBeenShown(tour.id)) continue;
    if (tour.state?.dismissed || tour.state?.completed) continue;
    if (!tourApplies(tour, context.url, traits)) continue;
    if (!triggerFires(tour.snapshot.triggers, context)) continue;
    return tour;
  }
  return null;
}
